/* eslint-disable operator-linebreak */
import CssBaseline from '@mui/material/CssBaseline';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import 'dayjs/locale/en';
import 'dayjs/locale/es';
import updateLocale from 'dayjs/plugin/updateLocale';

dayjs.extend(updateLocale);
dayjs.updateLocale('es', {
    months: [
        'Enero',
        'Febrero',
        'Marzo',
        'Abril',
        'Mayo',
        'Junio',
        'Julio',
        'Agosto',
        'Septiembre',
        'Octubre',
        'Noviembre',
        'Diciembre'
    ],
    monthsShort: ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic']
});
import { ThemeProvider, createTheme } from '@mui/material/styles';
// import { toRelativeUrl } from '@okta/okta-auth-js';
import { Security } from '@okta/okta-react';
import { ErrorInfo, memo, useEffect, useMemo, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from './common/hooks';
import GlobalSpinnerOverlay from './components/organisms/GlobalSpinnerOverlay';
import ErrorBoundaryComponent from './components/organisms/ErrorBoundary';
import { breakpoints, typography } from './themes';
// import ErrorPopup from './components/molecules/error-modal';
import AppRoutes from './routes';
// import EulaContainer from './containers/eula';
import { AuthProvider } from '../src/auth';
import { allowedDomainNameOrigins } from './common/constants';
import { AlertDialog } from './components/molecules/AlertDialog/AlertDialog';
import Timeout from './components/molecules/Timeout';
import { getLoggedInUserInfo } from './features/auth/authSlice';
import { syncAdviceClient } from './features/client';
import { syncAdviceGoals } from './features/client-goals';
import { getFeatureFlags, logEventToBackEnd } from './features/global/globalSlice';
import oktaAuth from './services/configs/oktaAuth';
import { overrides } from './themes/overrides';
import { CacheProvider } from '@emotion/react';
import createCache from '@emotion/cache';
import { setNavigate } from './common/helper';
import ConsentManager from './features/consent/ConsentManager';
import { getNewPalette } from './themes/newPalette';

function App() {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    // const [showErrorPopup, setShowErrorPopup] = useState(false);
    const [showExternalLinkModal, setShowExternalLinkModal] = useState('');
    const { i18n, t } = useTranslation();
    const isAuthenticated = useAppSelector((state) => state.auth.isAuthenticated);
    const tenantsList = useAppSelector((state) => state?.auth?.user?.tenants);
    const tenant = useAppSelector((state) => state?.auth?.user?.tenant);
    const userRole = useAppSelector((state) => state?.auth?.user?.userRole);

    const themePalette = useAppSelector((state) => state?.global?.globalConfig?.themePalette);
    const adviceClient = useAppSelector((state) => state?.adviceClient);
    const adviceGoals = useAppSelector((state) => state?.adviceGoals);
    const [updatedPalette, setUpdatedPalette] = useState(themePalette || getNewPalette('advice')); // default advice palette
    const currentLanguage = useAppSelector((state) => state?.global?.currentLanguage);

    useEffect(() => {
        dayjs.locale(currentLanguage);
    }, [currentLanguage]);

    useEffect(() => {
        setNavigate(navigate); // To enable navigation from plain functions
    }, [navigate]);

    // Sync i18n language with Redux on app initialization
    useEffect(() => {
        const savedLanguage = localStorage.getItem('userLanguage') || 'en';
        console.log('🌐 [App.tsx] Syncing language on mount:', {
            i18nLanguage: i18n.language,
            reduxLanguage: currentLanguage,
            localStorageLanguage: savedLanguage
        });

        // Ensure i18n and Redux are in sync
        if (i18n.language !== currentLanguage) {
            console.log('🌐 [App.tsx] Syncing i18n to match Redux:', currentLanguage);
            i18n.changeLanguage(currentLanguage || savedLanguage);
        }
    }, []); // Run once on mount

    // useLayoutEffect(() => {
    //     if (isEmpty(globalConfig?.styles)) {
    //         dispatch(updateGlobalLoaderState(true));
    //     }
    // }, []);

    useEffect(() => {
        // Get logged in user info
        const fetchUserInfo = async () => {
            if (isAuthenticated && !userRole && !tenantsList && !tenant) {
                await Promise.allSettled([dispatch(getFeatureFlags()), dispatch(getLoggedInUserInfo())]);
            }
        };
        fetchUserInfo();
    }, [isAuthenticated]);

    useEffect(() => {
        const handleExternalLinkClicked = (event: any) => {
            if (
                event.target.tagName === 'A' &&
                event.target.getAttribute('target') === '_blank' &&
                new URL(event.target.href).origin !== window.location.origin &&
                !allowedDomainNameOrigins.includes(new URL(event.target.href).origin)
            ) {
                event.preventDefault();
                setShowExternalLinkModal(event.target.href);
            }
        };
        window.addEventListener('click', handleExternalLinkClicked);
        return () => window.removeEventListener('click', handleExternalLinkClicked);
    }, []);

    useEffect(() => {
        // Update subdomain tenant to adviceClient and adviceGoals in redux store so that
        // all clients & goals related api calls automatically use current tenant
        if (tenant) {
            dispatch(syncAdviceClient({ ...adviceClient, tenant: tenant || 'advice' }));
            dispatch(syncAdviceGoals({ ...adviceGoals, tenant: tenant || 'advice' }));
            // dispatch(getTenantBasedConfig({ tenantId: tenant || 'advice', i18n }))
            // set updated palette as per tenant
            setUpdatedPalette(themePalette || getNewPalette(tenant));
        }
    }, [tenant, themePalette]);

    const memoizedThemeValue = useMemo(
        () => ({
            palette: updatedPalette,
            typography,
            breakpoints,
            shape: { borderRadius: 2 }
        }),
        [updatedPalette]
    );

    const theme = createTheme(memoizedThemeValue);
    theme.components = overrides(theme);

    // useEffect(() => {
    //     if (!isEmpty(error) && showErrorModal) {
    //         setShowErrorPopup(showErrorModal);
    //     } else {
    //         setShowErrorPopup(showErrorModal as any);
    //     }
    // }, [error, showErrorModal]);

    // const customAuthHandler = () => {
    //     navigate('/login');
    // };

    const restoreOriginalUri = async (_oktaAuth: any, originalUri: string) => {
        console.log('restoreOriginalUri check:', { originalUri, windowLocation: window.location.origin });
        // navigate(toRelativeUrl(originalUri || '', window.location.origin), { replace: true });
    };

    // Create emotion cache with nonce - read directly from window
    const emotionCache = useMemo(() => {
        const nonce = (window as any).__nonce;
        console.log('Creating emotion cache with nonce:', nonce || 'none');

        return createCache({
            key: 'mui',
            nonce: nonce || undefined, // Only set nonce if available
            prepend: true
        });
    }, []); // No dependencies needed since nonce is static

    return (
        <>
            <ConsentManager />
            <CacheProvider value={emotionCache}>
                <ThemeProvider theme={theme}>
                    <LocalizationProvider
                        dateAdapter={AdapterDayjs}
                        adapterLocale={currentLanguage}
                        localeText={{
                            fieldMonthPlaceholder: (params: { contentType: string }) =>
                                params.contentType === 'letter' ? 'MMM' : 'MM'
                        }}
                    >
                        <ErrorBoundary
                            FallbackComponent={ErrorBoundaryComponent}
                            onError={(error: Error, info: ErrorInfo) => {
                                console.log(error, info);
                                dispatch(logEventToBackEnd('UI_CRASH'));
                            }}
                        >
                            <Security
                                oktaAuth={oktaAuth}
                                // onAuthRequired={customAuthHandler}
                                restoreOriginalUri={restoreOriginalUri}
                            >
                                <GlobalSpinnerOverlay />
                                <CssBaseline />
                                <AuthProvider>
                                    <AppRoutes />
                                </AuthProvider>
                                {isAuthenticated && <Timeout />}
                                {Boolean(showExternalLinkModal) && (
                                    <AlertDialog
                                        handleClose={() => setShowExternalLinkModal('')}
                                        onCancellation={() => setShowExternalLinkModal('')}
                                        open={Boolean(showExternalLinkModal)}
                                        type="confirmation"
                                        confirmButtonLabel="OK"
                                        content={t('EXTERNAL_LINK_MODAL_CONTENT')}
                                        onConfirmation={() => {
                                            window.open(showExternalLinkModal, '_blank', 'noopener,noreferrer');
                                            setShowExternalLinkModal('');
                                        }}
                                        title={t('EXTERNAL_LINK_MODAL_TITLE')}
                                        aria-label="close-session-expired-popup"
                                        data-testid="session-expired-modal-close-button"
                                    />
                                )}
                            </Security>
                        </ErrorBoundary>
                    </LocalizationProvider>
                </ThemeProvider>
            </CacheProvider>
        </>
    );
}

export default memo(App);
